/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * Semantic Surface Styles Utility
 *
 * Provides reusable style objects and props for semantic surfaces (badges, chips, hover states)
 * to avoid repetitive useMemo calls and direct DOM style mutations.
 *
 * @example
 * ```tsx
 * const styles = createSurfaceStyles(surfaces.primary, surfaces.primaryInteraction);
 *
 * // Use badge props
 * <Badge {...styles.badgeProps}>Label</Badge>
 *
 * // Use hover handlers (no direct DOM mutation)
 * <div {...styles.hoverProps}>Content</div>
 *
 * // Use individual style objects
 * <div style={styles.cardStyle}>Card</div>
 * ```
 */

import type { CSSProperties } from "react";

import type {
  SemanticInteractionColors,
  SemanticSurfaceColors,
} from "@/hooks/ui";

export interface SurfaceStyleProps {
  /** Props for Badge component with semantic styling */
  badgeProps: {
    style: CSSProperties;
    className?: string;
  };
  /** Props for Chip component with semantic styling */
  chipProps: {
    style: CSSProperties;
    className?: string;
  };
  /** Props for hoverable elements (uses state, not direct DOM mutation) */
  hoverProps: {
    style: CSSProperties;
    className: string;
  };
  /** Style object for cards with semantic background */
  cardStyle: CSSProperties;
  /** Style object for text with semantic color */
  textStyle: CSSProperties;
  /** Style object for strong text with semantic color */
  textStrongStyle: CSSProperties;
  /** Style object for muted text with semantic color */
  textMutedStyle: CSSProperties;
  /** Style object for icons with semantic color */
  iconStyle: CSSProperties;
  /** Style object for borders with semantic color */
  borderStyle: CSSProperties;
  /** Hover background color (for use with CSS variables or inline styles) */
  hoverBackground: string;
  /** Hover text color */
  hoverText: string;
  /** Hover border color */
  hoverBorder: string;
}

/**
 * Creates reusable style objects and props for a semantic surface.
 *
 * @param surface - Semantic surface colors
 * @param interaction - Semantic interaction colors (for hover states)
 * @returns Object containing badge props, chip props, hover props, and style objects
 */
export function createSurfaceStyles(
  surface: SemanticSurfaceColors,
  interaction: SemanticInteractionColors,
): SurfaceStyleProps {
  return {
    badgeProps: {
      style: {
        backgroundColor: surface.backgroundMuted,
        color: surface.textStrong,
        borderColor: surface.border,
      },
      className: "transition-colors",
    },
    chipProps: {
      style: {
        backgroundColor: surface.backgroundMuted,
        color: surface.textStrong,
        borderColor: surface.border,
      },
      className: "transition-colors",
    },
    hoverProps: {
      style: {
        backgroundColor: surface.background,
      },
      className: "transition-colors",
    },
    cardStyle: {
      backgroundColor: surface.backgroundMuted,
      borderColor: surface.border,
    },
    textStyle: {
      color: surface.text,
    },
    textStrongStyle: {
      color: surface.textStrong,
    },
    textMutedStyle: {
      color: surface.textMuted,
    },
    iconStyle: {
      color: surface.icon,
    },
    borderStyle: {
      borderColor: surface.border,
    },
    hoverBackground: interaction.hoverBackground,
    hoverText: interaction.hoverText,
    hoverBorder: interaction.hoverBorder,
  };
}

/**
 * Creates hover event handlers that use CSS custom properties instead of direct DOM mutation.
 * Returns handlers that set CSS variables on the element, which can be used with Tailwind or CSS.
 *
 * @param hoverBackground - Hover background color
 * @param hoverText - Optional hover text color
 * @param hoverBorder - Optional hover border color
 * @returns Object with onMouseEnter and onMouseLeave handlers
 */
export function createHoverHandlers(
  hoverBackground: string,
  hoverText?: string,
  hoverBorder?: string,
): {
  onMouseEnter: (event: React.MouseEvent<HTMLElement>) => void;
  onMouseLeave: (event: React.MouseEvent<HTMLElement>) => void;
} {
  return {
    onMouseEnter: (event) => {
      const element = event.currentTarget as HTMLElement;
      element.style.setProperty("--hover-bg", hoverBackground);
      if (hoverText) {
        element.style.setProperty("--hover-text", hoverText);
      }
      if (hoverBorder) {
        element.style.setProperty("--hover-border", hoverBorder);
      }
      // Apply the hover background directly as fallback
      element.style.backgroundColor = hoverBackground;
    },
    onMouseLeave: (event) => {
      const element = event.currentTarget as HTMLElement;
      element.style.removeProperty("--hover-bg");
      element.style.removeProperty("--hover-text");
      element.style.removeProperty("--hover-border");
      element.style.backgroundColor = "";
    },
  };
}

/**
 * Creates a style object for a hoverable row that uses CSS variables.
 * This is a cleaner alternative to direct DOM mutation.
 *
 * @param surface - Semantic surface colors
 * @param interaction - Semantic interaction colors
 * @param isSelected - Whether the row is selected
 * @param isEdited - Whether the row was edited
 * @returns Style object with CSS custom properties
 */
export function createRowStyle(
  surface: SemanticSurfaceColors,
  interaction: SemanticInteractionColors,
  isSelected = false,
  isEdited = false,
): CSSProperties {
  const baseStyle: CSSProperties = {
    borderBottomColor: surface.border,
    // Use CSS custom properties for hover states
    ["--hover-bg" as string]: interaction.hoverBackground,
    ["--hover-text" as string]: interaction.hoverText,
    ["--hover-border" as string]: interaction.hoverBorder,
  };

  if (isSelected) {
    return {
      ...baseStyle,
      backgroundColor: surface.background,
      color: surface.text,
    };
  }

  if (isEdited) {
    return {
      ...baseStyle,
      backgroundColor: surface.backgroundMuted,
      color: surface.text,
    };
  }

  return baseStyle;
}

/**
 * Type-safe helper to get badge style from a semantic surface.
 * This is a convenience function that matches the pattern in suppliers/[id]/page.tsx
 *
 * @param surface - Semantic surface colors
 * @returns CSSProperties for a badge
 */
export function getBadgeStyleFromSurface(
  surface: SemanticSurfaceColors,
): CSSProperties {
  return {
    backgroundColor: surface.backgroundMuted,
    color: surface.textStrong,
    borderColor: surface.border,
  };
}
